import { MONDAY, PlainDate, SUNDAY, TIMEUNIT_DAYS, TIMEUNIT_SECONDS } from "@kiss-solutions/plaindate";
import { isOverlapping } from "@kiss-solutions/plaindate/plainPeriod";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import useCommonState from "../../hooks/UseCommonState";
import { useLoggedInArtistLocale } from "../../hooks/useLoggedInArtistLocale";
import { i18_TIMELINE } from "../../i18nReferences";
import { addStartEnd, buildDayPeriod, convertToTimelineData, resolveOffSiteSlotForDay } from "../../models/timelineModel";
import { selectStudioTatOrtID } from "../../store/common/commonSelector";
import { loadTimelineAction } from "../../store/timeline/timelineActions";
import { selectTimelineArtistDataMap, selectTimelineHolidays } from "../../store/timeline/timelineSelectors";
import "../common/listScrollbar.css";
import ListDialogContainer from "../lists/ListDialogContainer";
import Timeline from "./Timeline";
import TimelineDetailsDialog from "./TimelineDetailsDialog";
import TimelineSelection from "./TimelineSelection";

const getStartOfWeek = (start) => {
    const pd = new PlainDate(start).startOfDay();
    if (pd.getDayOfWeek() !== MONDAY) {
        return pd.toDayOfWeek(MONDAY, false);
    }
    return pd;
};

const TimelineContainer = () => {
    const [searchParams] = useSearchParams()
    const [startOfWeek, setStartOfWeek] = useState(getStartOfWeek(searchParams?.get('start')));
    const [tatortId, setTatortId] = useState("");
    const [times, setTimes] = useState(["08:00", "20:00"]);
    const { t } = useTranslation([i18_TIMELINE]);
    const [detailData, setDetailData] = useState(null);
    const isDetailDialogOpen = Boolean(detailData);
    const theme = useTheme();
    const { artistMap, tatortMap, termintypMap } = useCommonState();
    const locale = useLoggedInArtistLocale()
    const studioTatortId = useSelector((state) => selectStudioTatOrtID(state));
    const holidays = useSelector((state) => selectTimelineHolidays(state));
    const artistData = useSelector((state) => selectTimelineArtistDataMap(state));
    const dispatch = useDispatch();
    
    // Use derived state instead of synchronizing in useEffect
    const effectiveTatortId = tatortId || studioTatortId;

    useEffect(() => {
        if (startOfWeek == null) {
            return;
        }
        if (effectiveTatortId == null || effectiveTatortId === "") {
            return;
        }
        dispatch(
            loadTimelineAction({
                tatortId: effectiveTatortId,
                datVon: startOfWeek.toDateString(),
                datBis: new PlainDate(startOfWeek).toDayOfWeek(SUNDAY).add(1, TIMEUNIT_DAYS).toDateString(),
            })
        );
    }, [effectiveTatortId, startOfWeek, dispatch]);
    const targetPeriod = useMemo(
        () =>
            startOfWeek != null && {
                start: new PlainDate(startOfWeek),
                end: new PlainDate(startOfWeek).toDayOfWeek(SUNDAY).add(1, TIMEUNIT_DAYS),
            },
        [startOfWeek]
    );
    const timelineData = useMemo(
        () =>
            artistData != null &&
            targetPeriod != null &&
            termintypMap != null &&
            artistData.map((a) =>
                convertToTimelineData({
                    tatortMap,
                    artistMap,
                    targetPeriod,
                    timelineArtistData: a,
                    slotSizeFactor: 60,
                    termintypMap,
                    startTimeString: times[0],
                    endTimeString: times[1],
                })
            ),
        [artistData, targetPeriod, termintypMap, tatortMap, artistMap, times]
    );
    const isDark = useMemo(() => theme?.palette.mode === "dark", [theme]);
    const navigate = useNavigate();

    // Navigate to calendar with current date as URL param.
    // Calendar will initialize itself from URL (single source of truth).
    const handleExit = useCallback(() => {
        const dateParam = startOfWeek?.toDateString();
        if (dateParam) {
            navigate({
                pathname: "/",
                search: `?${createSearchParams({ date: dateParam })}`,
            });
        } else {
            navigate("/");
        }
    }, [startOfWeek, navigate]);
    const handleClickDay = useCallback(
        (detailParams) => {
            const date = new PlainDate(startOfWeek).add(detailParams.dayIndex);
            const dayPeriod = buildDayPeriod(date, times[0], times[1]);
            const notAvailableSlots = artistData[detailParams.dataIndex].notAvailableAtLocation.map((s) => addStartEnd(s));
            const offSiteSlot = resolveOffSiteSlotForDay(notAvailableSlots, dayPeriod);
            const isOffsite = Boolean(offSiteSlot);
            const location = isOffsite
                ? tatortMap[offSiteSlot.TatOrtID]?.Ort
                : tatortMap[effectiveTatortId]?.Ort;
            const bookedSlots = artistData[detailParams.dataIndex].bookedSlots
                .map((s) => addStartEnd(s))
                .filter((s) => isOverlapping(dayPeriod, s, TIMEUNIT_SECONDS, false));
            setDetailData({
                Kuerzel: artistMap[detailParams.ArtistID].Kuerzel,
                ArtistID: detailParams.ArtistID,
                date: date.toLocalDate(),
                location,
                isOffsite,
                bookedSlots,
            });
        },
        [artistData, artistMap, startOfWeek, effectiveTatortId, tatortMap, times]
    );
    const handleClickToday = useCallback(() => setStartOfWeek(getStartOfWeek(new PlainDate())), [])
    const handleCloseDetailDialog = useCallback(() => setDetailData(null), []);
    const handleChangeTimes = useCallback((e) => setTimes(e), []);
    // Navigate to calendar with selected artist and current date as URL params.
    // Calendar will initialize itself from URL (single source of truth).
    const handleClickArtist = useCallback(
        (artist) => {
            const dateParam = startOfWeek?.toDateString();
            const params = { artist: String(artist.ArtistID) };
            if (dateParam) {
                params.date = dateParam;
            }
            navigate({
                pathname: "/",
                search: `?${createSearchParams(params)}`,
            });
        },
        [navigate, startOfWeek]
    );
    return (
        (<ListDialogContainer onExit={handleExit} header={t("TIMELINE.HEADER", { ns: i18_TIMELINE })}>
            <Box sx={{
                width: "100%"
            }}>
                <TimelineSelection
                    start={startOfWeek}
                    tatortMap={tatortMap}
                    onChangeStart={(e) => setStartOfWeek(e)}
                    tatortId={effectiveTatortId}
                    onChangeTatortId={(e) => setTatortId(e)}
                    onChangeTimes={handleChangeTimes}
                    onClickToday={handleClickToday}
                />
            </Box>
            <Box
                className={isDark ? "darkScrollbars" : "lightScrollbars"}
                sx={{
                    flex: "1",
                    minHeight: "0"
                }}>
                <Box
                    sx={{
                        overflow: "auto",
                        height: "100%",
                        padding: "2em 0"
                    }}>
                    <Timeline
                        timelineData={timelineData}
                        holidays={holidays}
                        locale={locale}
                        start={startOfWeek}
                        isDark={isDark}
                        onClickDay={handleClickDay}
                        onClickArtist={handleClickArtist}
                    />
                </Box>
            </Box>
            <TimelineDetailsDialog
                t={t}
                locale={locale}
                onClose={handleCloseDetailDialog}
                open={isDetailDialogOpen}
                detailData={detailData}
            />
        </ListDialogContainer>)
    );
};

export default TimelineContainer;
